import { Link } from "react-router-dom";
import { Mail, Phone, Linkedin, MailPlus, Facebook, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/content";

const FOOTER_COLS = [
  {
    title: "Language Services",
    links: [
      { label: "Translation", to: "/services#translation" },
      { label: "Editing & Proofreading", to: "/services#editing-proofreading" },
      { label: "Localization", to: "/services#localization" },
      { label: "And more...", to: "/services#language" },
    ],
  },
  {
    title: "AI Solutions",
    links: [
      { label: "MTPE", to: "/services#mtpe" },
      { label: "AI Content Review & QA", to: "/services#ai-content-review" },
      { label: "AI Localization Review", to: "/services#ai-localization-review" },
      { label: "And more...", to: "/services#ai" },
    ],
  },
  {
    title: "Industries",
    links: [
      { label: "Healthcare & Medical", to: "/industries/healthcare" },
      { label: "Legal & Compliance", to: "/industries/legal" },
      { label: "Finance & Banking", to: "/industries/finance" },
      { label: "And more...", to: "/industries" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Languages", to: "/languages" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#04132F] text-white"
    >
      {/* Top CTA strip */}
      <div className="border-b border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="text-2xl lg:text-3xl font-semibold text-white tracking-tight">
              Ready to communicate across 85+ languages?
            </h3>
            <p className="mt-2 text-sm text-white/70 max-w-xl">
              Talk to a Petraloc specialist about your translation, localization, or AI language project.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="footer-cta-quote"
            className="inline-flex items-center gap-2 bg-[#00D1B2] hover:bg-[#00A990] text-white px-6 py-3 rounded-md text-[13px] font-semibold tracking-wide transition-colors w-fit"
          >
            Request a Quote
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-12 lg:py-14">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-3">
            <Link to="/" className="inline-flex items-center">
              <img src={BRAND.logoWhite} alt="Petraloc" className="h-9 w-auto" />
            </Link>
            <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-xs">
              Enterprise language intelligence — certified translation, interpretation,
              and AI-powered localization across 85+ languages.
            </p>

            <div className="mt-6 space-y-2 text-[13px]">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white"
              >
                <Mail className="w-3.5 h-3.5 text-[#00D1B2]" />
                {BRAND.email}
              </a>
              <a
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                data-testid="footer-phone"
                className="flex items-center gap-2 text-white/80 hover:text-white"
              >
                <Phone className="w-3.5 h-3.5 text-[#00D1B2]" />
                {BRAND.phone}
              </a>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                 className="w-8 h-8 rounded-md border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors">
                <Linkedin className="w-3.5 h-3.5" />
              </a>
              <a href={`mailto:${BRAND.email}?subject=Subscribe%20to%20Petraloc%20updates`} aria-label="Newsletter"
                 data-testid="footer-social-newsletter"
                 className="w-8 h-8 rounded-md border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors">
                <MailPlus className="w-3.5 h-3.5" />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                 className="w-8 h-8 rounded-md border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors">
                <Facebook className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {FOOTER_COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white mb-4">
                {col.title}
              </h4>
              <ul className="space-y-2 text-[13px]">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-white/65 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-white/55">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} Petraloc · All rights reserved.
          </p>
          <nav data-testid="footer-legal-nav" className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/support" className="hover:text-white">Support</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
